import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useTranslation } from "react-i18next";
import StepRowCard from "../components/stepRowCard";
import StepContainer from "../stepContainer";
import AddButton from "../components/addButton";
import StepDrawer from "../stepDrawer";
import { GRID_NAMES } from "../../../constants/gridNames";
import { API_PARAM } from "../../../constants/apiParams";
import DetailsDialogs from "../components/detailsDialogs";
import { saveStep } from "../../../services/profileService";
import { calculateAdvancedQuery } from "../utils/calculateAdvancedQuery";
import { profileActions } from "../../../redux/reducers/profileReducer";
import { useDispatch } from "react-redux";

const SkillGoalsSelectionPage = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const [skillGoals, setSkillGoals] = useState([]);
  const [initialSkillGoals, setInitialSkillGoals] = useState([]);
  const [drawerOpen, setdrawerOpen] = useState(false);
  const [dialog, setDialog] = useState({ open: false });
  const [gridInitialFilter, setGridInitialFilter] = useState(null);
  const dispatch = useDispatch();

  // On step load
  useEffect(() => {
    props.setProgressValue(90);
    if (props.existingData) {
      setSkillGoals(props.existingData);
      setInitialSkillGoals(props.existingData);
    }
  }, []);

  useEffect(() => {
    let filterModel = calculateAdvancedQuery(skillGoals);
    setGridInitialFilter(filterModel);
  }, [skillGoals.length]);

  const onStepClose = async (goStepBack = false, stepCompleted = true) => {
    // All the skill goals that are deleted or added
    let updatedSkillGoals = [];
    // Check for skill goals that user deleted
    initialSkillGoals.forEach((skillGoal) => {
      if (!skillGoals.find((x) => x.id === skillGoal.id)) {
        updatedSkillGoals.push({
          id: skillGoal.id,
          isDeleted: true,
        });
      }
    });
    // Check for skill goals that user updated the proficiency Level
    skillGoals.forEach((skillGoal) => {
      const initSkill = initialSkillGoals.find((x) => x.id === skillGoal.id);
      if (
        initSkill &&
        initSkill.proficiencyLevel !== skillGoal.proficiencyLevel
      ) {
        updatedSkillGoals.push({
          id: skillGoal.id,
          proficiencyLevel: skillGoal.proficiencyLevel,
        });
      }
    });
    // Check for new skill goals that user added
    skillGoals.forEach((skillGoal) => {
      if (!initialSkillGoals.find((x) => x.id === skillGoal.id)) {
        updatedSkillGoals.push({
          skillId: skillGoal.id,
          proficiencyLevel: skillGoal.proficiencyLevel,
          isNew: true,
        });
      }
    });
    let data = {
      currentStep: 3,
      isStepCompleted: stepCompleted,
      updatedSkillGoals: updatedSkillGoals,
    };
    // Request to save the user's Skill Goals and then navigate to next step or previous step
    await saveStep(data);
    // Save the skillGoals in local state so not do another request to get the data
    dispatch(profileActions.setSkillGoals(skillGoals));
    if (stepCompleted) {
      if (goStepBack) props.navigateStepBack();
      else props.navigateStepFront();
    }
  };

  useImperativeHandle(ref, () => ({
    onStepClose,
  }));

  // Add new skills to the list
  const addNewSkills = (newSkills) => {
    const skillsToAdd = [];
    newSkills.forEach((newSkill) => {
      const existingSkill = skillGoals.find(
        (skill) => skill.id === newSkill.id
      );
      if (!existingSkill) {
        const skillToAdd = {
          ...newSkill,
          proficiencyLevel: 1,
        };
        skillsToAdd.push(skillToAdd);
      }
    });
    setSkillGoals((prevSkills) => [...skillsToAdd, ...prevSkills]);
  };

  // Delete skill from the list
  const handleDeleteSkill = (skillId) => {
    setSkillGoals(skillGoals.filter((skill) => skill.id !== skillId));
  };

  // Update skill proficiency level
  const setSkillLevel = (skillid, level) => {
    const updatedSkills = skillGoals.map((skill) => {
      if (skill.id === skillid) {
        return { ...skill, proficiencyLevel: level };
      }
      return skill;
    });
    setSkillGoals(updatedSkills);
  };

  // Grid columns definitions for the Skill Goals step drawer
  const columnDefs = [
    {
      cellRenderer: "CustomRadioCell",
      maxWidth: 40,
    },
    {
      field: "name",
      filter: true,
      cellRenderer: "CustomNameCell",
      autoHeight: true,
      maxWidth: 550,
    },
    {
      field: "category",
      hide: true,
      selectedFilterColumn: true,
      rowGroup: true,
    },
  ];

  return (
    <>
      {dialog?.open && <DetailsDialogs dialog={dialog} setDialog={setDialog} />}
      <StepDrawer
        open={drawerOpen}
        header={t("profileScreen.drawer.addSkillGoalsHeader")}
        searchLabel={t("profileScreen.drawer.searchSkillsLabel")}
        filterLabel={t("profileScreen.drawer.searchSkillsFilterLabel")}
        onClose={() => setdrawerOpen(false)}
        onSave={addNewSkills}
        isSkillDrawer
        initialGridState={
          gridInitialFilter
            ? { filter: { filterModel: gridInitialFilter } }
            : null
        }
        columnDefs={columnDefs}
        gridName={GRID_NAMES.SKILL_GOALS_SELECTION}
        datasourceUrl={API_PARAM.AVAILABLE_SKILL_GOALS}
        filterValuesUrl={API_PARAM.AVAILABLE_SKILL_GOAL_VALUES}
      />
      <StepContainer
        stepTitle={t("profileScreen.skillGoalsStep.selection.title")}
        stepDescription={t(
          "profileScreen.skillGoalsStep.selection.description"
        )}
        onTransparentButtonClick={() => onStepClose(true)}
        transparentButtonLabel={t("profileScreen.btnBack")}
        onSecondaryButtonClick={onStepClose}
        secondaryButtonLabel={t("profileScreen.btnNext")}
        hasLargeHeader
        header={
          <AddButton
            label={t("profileScreen.skillGoalsStep.btnAddSkillGoal")}
            onClick={() => setdrawerOpen(true)}
          />
        }
      >
        {skillGoals &&
          skillGoals.map((skill) => (
            <StepRowCard
              key={skill.id}
              title={skill.name}
              description={skill.category}
              hasHover
              hasLevels={props.skillProficiencyLevelsEnabled}
              level={skill.proficiencyLevel}
              setLevel={(level) => setSkillLevel(skill.id, level)}
              hasDelete
              onDelete={() => handleDeleteSkill(skill.id)}
              onInfoClicked={() => setDialog({ open: true, skillId: skill.id, proficiencyLevel: skill.proficiencyLevel})}
            />
          ))}
      </StepContainer>
    </>
  );
});

export default SkillGoalsSelectionPage;
